import React from 'react';
import classNames from 'classnames';
import { observer } from 'mobx-react';
import { Modal } from 'ui/base/modal';
import { TextInput } from 'ui/base/input';
import { Theme, ThemedStyles, useStyles } from 'ui/base/theme';
import { ScrollableArea } from 'ui/base/page/scrollable_area';
import { BackButton } from 'ui/base/button/back_button';
import { SearchInputStore } from './search_input_store';
import baseStyles from './search_input.module.css';

type SearchInputStyles = {
  placeholder: string;
  textInput: string;
  hidden: string;
  modalContainer: string;
  column: string;
  headerContainer: string;
  listContainer: string;
};

const themedStyles: ThemedStyles<SearchInputStyles> = {
  [Theme.DAYLIGHT]: {
    placeholder: baseStyles.daylightPlaceholder,
    textInput: baseStyles.daylightTextInput,
    hidden: baseStyles.hidden,
    modalContainer: baseStyles.modalContainer,
    column: baseStyles.column,
    headerContainer: baseStyles.headerContainer,
    listContainer: baseStyles.listContainer,
  },
  [Theme.MIDNIGHT]: {
    placeholder: baseStyles.midnightPlaceholder,
    textInput: baseStyles.midnightTextInput,
    hidden: baseStyles.hidden,
    modalContainer: baseStyles.modalContainer,
    column: baseStyles.column,
    headerContainer: baseStyles.headerContainer,
    listContainer: baseStyles.listContainer,
  },
};

export type SearchItemProps<T> = {
  key: string;
  data: T;
};

type SearchModalProps<T> = {
  store: SearchInputStore;
  placeholder: string;
  data: readonly T[];
  filterFunc: (item: T, text: string) => boolean;
  keyFunc: (item: T) => string;
  ItemComponent: React.ComponentType<SearchItemProps<T>>;
  selectOnly?: boolean;
};

export const SearchModal = observer(<T, >(props: SearchModalProps<T>) => {
  const {
    store,
    placeholder,
    data,
    filterFunc,
    keyFunc,
    ItemComponent,
    selectOnly = true,
  } = props;
  const styles = useStyles(themedStyles);
  const trueFilterFunc = (item: T) => filterFunc(item, store.search.value);

  return (
    <div className={styles.modalContainer}>
      <ScrollableArea>
        <div className={styles.column}>
          <div className={styles.headerContainer}>
            <BackButton onClick={store.modal.hideModal}/>
            <TextInput
              store={store.search}
              placeholder={placeholder}
              className={classNames(styles.textInput, selectOnly && styles.hidden)}
            />
          </div>
          <div className={styles.listContainer}>
            {
              data.filter(trueFilterFunc).map((item) => <ItemComponent data={item} key={keyFunc(item)}/>)
            }
          </div>
        </div>
      </ScrollableArea>
    </div>
  );
});

type SearchInputProps<T> = { DisplayComponent: React.ComponentType } & SearchModalProps<T>;

export const SearchInput = <T, >(props: SearchInputProps<T>) => {
  const {
    DisplayComponent,
    store,
    ...modalProps
  } = props;

  return (
    <>
      <Modal store={store.modal}>
        <SearchModal store={store} {...modalProps}/>
      </Modal>
      <DisplayComponent/>
    </>
  );
};
